import { ApplicationRef, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { SwUpdate } from '@angular/service-worker';
import { BehaviorSubject, filter, first, firstValueFrom } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { CoreProvider } from './core.provider';
import { CUSTOM_REQUEST_AUTHORIZATION } from './auth/auth.interceptor';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';

/** Shape of `assets/version.json`, kept in sync by grunt on every release. */
interface VersionManifest {
  version: string;
  platform?: {
    pwa_version?: string;
    ios_version?: string;
    android_version?: string;
    desktop_runtime_version?: string;
  };
}

const STORE_URLS = {
  android: 'https://play.google.com/store/apps/details?id=ai.handball.haibench',
  ios: 'https://apps.apple.com/app/apple-store/id6747640245',
};

/** How often the app re-checks for a newer release. */
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Detects that a newer release is available and applies it.
 *
 * Two independent signals are combined:
 * - On web/PWA the authoritative one is the service worker: `VERSION_READY` means the new
 *   assets are already downloaded, so activating + reloading actually lands on the new build.
 * - On native (Cordova) there is no service worker, so `assets/version.json` is polled and
 *   compared against the installed version; the fix there is a trip to the store.
 */
@Injectable({
  providedIn: 'root',
})
export class VersionService {

  private core?: CoreProvider;
  private checkInterval?: ReturnType<typeof setInterval>;

  /** True once an update is both available and actionable for the current platform. */
  public readonly updateAvailable$ = new BehaviorSubject<boolean>(false);

  private latestVersionData?: string;
  /** Version advertised by `assets/version.json` for this platform, when known. */
  public get latestVersion(): string | undefined {
    return this.latestVersionData;
  }

  constructor(
    private readonly http: HttpClient,
    private readonly appRef: ApplicationRef,
    private readonly swUpdate: SwUpdate,
  ) {
    // Subscribed here rather than in initCore so no early event is missed.
    this.swUpdate.versionUpdates.pipe(
      filter(event => event.type === 'VERSION_READY'),
    ).subscribe(event => {
      console.log('New app version downloaded and ready to activate', event);
      this.updateAvailable$.next(true);
    });
  }

  public async initCore(core: CoreProvider) {
    this.core = core;
    Sentry.setTag('appVersion', core.version);

    void this.checkForUpdates();
    // Wait for stability before scheduling the poll, otherwise the interval keeps Zone.js
    // permanently unstable and `isStable` never fires for anything else.
    this.appRef.isStable.pipe(first(stable => stable)).subscribe(() => this.startUpdateInterval());
  }

  private startUpdateInterval() {
    this.stopUpdateInterval();
    this.checkInterval = setInterval(() => void this.checkForUpdates(), CHECK_INTERVAL_MS);
  }

  public stopUpdateInterval() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = undefined;
    }
  }

  /** Store listing to send the user to, or undefined when there is none for this platform. */
  private get storeUrl(): string | undefined {
    if (this.core?.isAndroid) return STORE_URLS.android || undefined;
    if (this.core?.isIOS) return STORE_URLS.ios || undefined;
    return undefined;
  }

  public async checkForUpdates() {
    if (this.swUpdate.isEnabled) {
      try {
        await this.swUpdate.checkForUpdate();
      } catch (error) {
        console.error('Error checking for service worker update', error);
        Sentry.captureException(error);
      }
    }

    try {
      const manifest = await firstValueFrom(this.http.get<VersionManifest>('assets/version.json', {
        // Never let the service worker answer this one, it is the freshness probe itself.
        headers: { 'ngsw-bypass': 'true' },
        // Skip waiting for token initialization; this endpoint is public.
        context: new HttpContext().set(CUSTOM_REQUEST_AUTHORIZATION, 'no-auth-required'),
      }));

      const latest = this.core?.isIOS
        ? manifest.platform?.ios_version
        : this.core?.isAndroid
          ? manifest.platform?.android_version
          : manifest.platform?.pwa_version;
      this.latestVersionData = latest ?? manifest.version;
      console.debug('Latest available version', this.latestVersionData, 'installed', this.core?.version);

      // On web the service worker decides: announcing an update before its assets are
      // downloaded would reload into the very same build. `VERSION_READY` handles it.
      if ((this.core?.isAndroid || this.core?.isIOS)
        && !!this.storeUrl
        && this.latestVersionData !== this.core?.version) {
        this.updateAvailable$.next(true);
      }
    } catch (error) {
      console.error('Error fetching version.json', error);
      Sentry.captureException(error);
    }
  }

  /** Applies the pending update: reload into it on web, store listing on native. */
  public async update() {
    const storeUrl = this.storeUrl;
    if (storeUrl) {
      this.core?.openExternalUrl(storeUrl, '_blank');
      return;
    }

    try {
      await this.swUpdate.activateUpdate();
      location.reload();
    } catch (error) {
      console.error('Error activating app update', error);
      Sentry.captureException(error);
      await this.core?.errorToast(undefined, this.core!.trans.instant(_('Could not update the app. Please reload the page.')));
    }
  }

  public getFullVersionString(): string {
    const platform = this.core?.isAndroid ? 'android' : this.core?.isIOS ? 'ios' : 'pwa';
    return `${platform}_v${this.core?.version}`;
  }

}
